/**
 * A self-registered doctor's practice asking for the full ClinVea clinic software with its own clinic
 * administrator (doctor side), and the super admin's review queue.
 */
import { fetchApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type ClinicAccountRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN';

export interface ClinicPlanOption {
  id: string;
  code: string;
  name: string;
  monthlyPrice?: number | null;
  annualPrice?: number | null;
  perDoctorPrice?: number | null;
  /** null = unlimited */
  maxDoctors?: number | null;
  maxStaff?: number | null;
  maxBranches?: number | null;
  maxPatients?: number | null;
  maxAiCalls?: number | null;
  features?: string[];
  modules?: { code: string; label: string }[];
  /** For a given practice: whether the plan holds its doctors, its staff and the administrator it will invite. */
  fits?: boolean | null;
  fitNote?: string | null;
}

/** Who the plan must hold: the practice's active doctors and staff, plus the administrator the approval invites. */
export interface PlanUsage {
  doctors: number;
  staff: number;
  adminSeatNeeded: boolean;
}

export interface PlanCatalog {
  plans: ClinicPlanOption[];
  usage: PlanUsage;
}

export interface ClinicAccountRequestView {
  id: string;
  clinicId: string;
  clinicName?: string | null;
  city?: string | null;
  status: ClinicAccountRequestStatus;
  /** Empty when the doctor runs the clinic themselves (selfManaged). */
  adminName?: string | null;
  adminEmail?: string | null;
  adminMobile?: string | null;
  preferredPlanId?: string | null;
  preferredPlanName?: string | null;
  preferredBillingCycle?: 'MONTHLY' | 'ANNUAL' | null;
  /** Only on requests made before 2026-10-02; the plan decides the seats now. */
  doctorsExpected?: number | null;
  staffExpected?: number | null;
  message?: string | null;
  decisionNote?: string | null;
  approvedPlanName?: string | null;
  /** Super-admin view only. */
  requestedByName?: string | null;
  createdAt: string;
  decidedAt?: string | null;
  /** The doctor will run the clinic themselves: approval makes them its owner doctor (medopd-docs 148). */
  selfManaged?: boolean;
}

export interface ClinicAccountRequestInput {
  clinicId: string;
  /** Required unless selfManaged. */
  adminName?: string;
  adminEmail?: string;
  adminMobile?: string;
  preferredPlanId?: string;
  preferredBillingCycle?: 'MONTHLY' | 'ANNUAL';
  message?: string;
  /** The doctor will run the clinic themselves; no administrator is invited (medopd-docs 148). */
  selfManaged?: boolean;
}

/** {@code localPassword}: local development only, where no invitation email is sent. */
export interface ClinicAccountApproved {
  request: ClinicAccountRequestView;
  invitationEmailed: boolean;
  localPassword?: string | null;
}

// Bodies carry the administrator's email and mobile, so they're kept out of the dev-console request log.
const json = (method: string, body?: unknown) => ({
  method, body: body === undefined ? undefined : JSON.stringify(body), skipBodyLog: true,
});
const DOCTOR = '/api/doctor/clinic-account-requests';
const ADMIN = '/api/admin/clinic-account-requests';

export const clinicAccountRequestsModule = {
  doctor: {
    mine: () => fetchApi<ClinicAccountRequestView[]>(DOCTOR),
    /** The plans in full, with whether each one fits this practice. */
    plans: (clinicId: string, selfManaged = false) =>
      fetchApi<PlanCatalog>(`${DOCTOR}/plans?clinicId=${encodeURIComponent(clinicId)}&selfManaged=${selfManaged}`),
    submit: (input: ClinicAccountRequestInput) => fetchApi<ClinicAccountRequestView>(DOCTOR, json('POST', input)),
    withdraw: (id: string) => fetchApi<ClinicAccountRequestView>(`${DOCTOR}/${encodeURIComponent(id)}/withdraw`, json('POST')),
  },
  admin: {
    list: (status: 'PENDING' | 'ALL') => fetchApi<ClinicAccountRequestView[]>(`${ADMIN}?status=${status}`),
    /** One page with totals (medopd-docs 112); list() stops at 200. */
    listPage: (status: 'PENDING' | 'ALL', page: number, size: number) =>
      fetchApi<PagedResponse<ClinicAccountRequestView>>(`${ADMIN}?status=${status}&page=${page}&size=${size}`),
    /** The plans in full for one request's practice, with whether each one fits it. */
    plansFor: (requestId: string) => fetchApi<PlanCatalog>(`${ADMIN}/${encodeURIComponent(requestId)}/plans`),
    approve: (id: string, planId: string, billingCycle: 'MONTHLY' | 'ANNUAL', note?: string) =>
      fetchApi<ClinicAccountApproved>(`${ADMIN}/${encodeURIComponent(id)}/approve`, json('POST', { planId, billingCycle, note })),
    reject: (id: string, note: string) =>
      fetchApi<ClinicAccountRequestView>(`${ADMIN}/${encodeURIComponent(id)}/reject`, json('POST', { note })),
  },
};
